import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CountrySearchService {
  private countriesSubject = new BehaviorSubject<string[]>([]);
  public countries$: Observable<string[]> = this.countriesSubject.asObservable();

  private readonly API_URL = `${environment.apiUrl}/countries/names`;

  constructor(private http: HttpClient) {
    this.loadCountries();
  }

  /**
   * Load all countries from backend API
   */
  private loadCountries(): void {
    this.http.get<string[]>(this.API_URL)
      .pipe(
        tap(countries => {
          console.log('Countries loaded from API:', countries.length, 'countries');
          this.countriesSubject.next(countries);
        }),
        catchError(error => {
          console.error('Error loading countries from API:', error);
          this.countriesSubject.next([]);
          return throwError(() => error);
        })
      )
      .subscribe();
  }

  /**
   * Search countries by query string
   * Performs local client-side search with relevance scoring
   * @param query Search string (minimum 3 characters)
   * @param scoringFunction Optional custom scoring function
   * @returns Array of matching countries, max 5 results
   */
  public searchCountries(
    query: string, 
    scoringFunction?: (text: string, query: string) => number
  ): string[] {
    if (query.length < 3) {
      return [];
    }

    const countries = this.countriesSubject.value;
    const defaultScore = scoringFunction || this.calculateRelevance.bind(this);

    // Score all countries
    const scoredCountries = countries
      .map(country => ({
        name: country,
        score: defaultScore(country, query)
      }))
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map(item => item.name);

    return scoredCountries;
  }

  /**
   * Calculate relevance score for a country name against query
   * Same logic as in app.ts for consistency
   */
  private calculateRelevance(text: string, query: string): number {
    const lowerText = text.toLowerCase().trim();
    const lowerQuery = query.toLowerCase().trim();

    // Exakter Match
    if (lowerText === lowerQuery) return 1000;

    // Beginnt mit Query (perfekt!)
    if (lowerText.startsWith(lowerQuery)) return 100;

    // Query ist Beginn eines Wortes (z.B. "Deutschland" bei Query "deu")
    const words = lowerText.split(/[\s\-_/]/);
    if (words.some(w => w.startsWith(lowerQuery))) return 80;

    // Query liegt irgendwo im Text
    if (lowerText.includes(lowerQuery)) return 40;

    // Keine Ähnlichkeit
    return 0;
  }
}
